import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, X, Filter, Clock, TrendingUp } from 'lucide-react';
import ArticleCard from '../components/news/ArticleCard';
import { searchService, articleService } from '../services/api';
import { useNewspaper } from '../context/AppContext';
import styles from './SearchPage.module.css';

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { categories } = useNewspaper();
  
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState({ articles: [], categories: [], authors: [] });
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [activeTab, setActiveTab] = useState('articles');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('relevance');
  const [trendingArticles, setTrendingArticles] = useState([]);

  const recentSearches = JSON.parse(localStorage.getItem('recentSearches') || '[]');

  useEffect(() => {
    loadTrending();
  }, []);

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setQuery(q);
      performSearch(q);
    }
  }, [searchParams]);

  const loadTrending = async () => {
    try {
      const { data } = await articleService.getAll({
        sortBy: 'views',
        sortOrder: 'desc',
        limit: 5
      });
      setTrendingArticles(data);
    } catch (error) {
      console.error('Error loading trending:', error);
    }
  };

  const performSearch = async (searchQuery) => {
    if (!searchQuery.trim()) return;

    try {
      setLoading(true);
      setSearched(true);

      const data = await searchService.search(searchQuery);
      setResults(data);

      // Guardar en búsquedas recientes
      saveRecentSearch(searchQuery);
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveRecentSearch = (searchQuery) => {
    const searches = JSON.parse(localStorage.getItem('recentSearches') || '[]');
    const updated = [searchQuery, ...searches.filter(s => s !== searchQuery)].slice(0, 5);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      setSearchParams({ q: query });
    }
  };

  const handleQuickSearch = (term) => {
    setQuery(term);
    setSearchParams({ q: term });
  };

  const clearSearch = () => {
    setQuery('');
    setSearchParams({});
    setResults({ articles: [], categories: [], authors: [] });
    setSearched(false);
  };

  const clearRecentSearches = () => {
    localStorage.removeItem('recentSearches');
    window.location.reload();
  };

  // Filtrar artículos por categoría
  const filteredArticles = selectedCategory === 'all'
    ? results.articles
    : results.articles.filter(a => a.category?.slug === selectedCategory);

  // Ordenar artículos
  const sortedArticles = [...filteredArticles].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.publishedAt) - new Date(a.publishedAt);
      case 'oldest':
        return new Date(a.publishedAt) - new Date(b.publishedAt);
      case 'popular':
        return (b.views || 0) - (a.views || 0);
      default:
        return 0; // relevance - mantener orden original
    }
  });

  const totalResults = results.articles.length + results.categories.length + results.authors.length;

  return (
    <main className={styles.searchPage}>
      {/* Header de búsqueda */}
      <header className={styles.header}>
        <div className="container">
          <h1 className={styles.title}>Buscar</h1>
          
          <form onSubmit={handleSubmit} className={styles.searchForm}>
            <div className={styles.searchInputWrapper}>
              <Search className={styles.searchIcon} size={20} />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar noticias, temas, autores..."
                className={styles.searchInput}
                autoFocus
              />
              {query && (
                <button 
                  type="button" 
                  className={styles.clearBtn}
                  onClick={clearSearch}
                >
                  <X size={18} />
                </button>
              )}
            </div>
            <button type="submit" className="btn btn-primary">
              Buscar
            </button>
          </form>
        </div>
      </header>

      <div className={styles.content}>
        <div className="container">
          {!searched ? (
            /* Estado inicial - antes de buscar */
            <div className={styles.initialState}>
              {/* Búsquedas recientes */}
              {recentSearches.length > 0 && (
                <section className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <h2>
                      <Clock size={18} />
                      Búsquedas recientes
                    </h2>
                    <button 
                      className={styles.clearLink}
                      onClick={clearRecentSearches}
                    >
                      Limpiar
                    </button>
                  </div>
                  <div className={styles.tagCloud}>
                    {recentSearches.map((term, index) => (
                      <button
                        key={index}
                        className={styles.searchTag}
                        onClick={() => handleQuickSearch(term)}
                      >
                        {term}
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {/* Trending */}
              <section className={styles.section}>
                <h2>
                  <TrendingUp size={18} />
                  Tendencias
                </h2>
                <div className={styles.trendingList}>
                  {trendingArticles.map((article, index) => (
                    <Link 
                      key={article.id}
                      to={`/articulo/${article.slug}`}
                      className={styles.trendingItem}
                    >
                      <span className={styles.trendingNumber}>{index + 1}</span>
                      <div className={styles.trendingContent}>
                        <span className={styles.trendingCategory}>
                          {article.category?.name}
                        </span>
                        <h3 className={styles.trendingTitle}>{article.title}</h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>

              {/* Categorías para explorar */}
              <section className={styles.section}>
                <h2>Explorar por sección</h2>
                <div className={styles.categoriesGrid}>
                  {categories.filter(c => c.slug !== 'portada').map(category => (
                    <Link
                      key={category.id}
                      to={`/seccion/${category.slug}`}
                      className={styles.categoryCard}
                      style={{ '--cat-color': category.color }}
                    >
                      <span className={styles.categoryIcon}>{category.icon}</span>
                      <span>{category.name}</span>
                    </Link>
                  ))}
                </div>
              </section>
            </div>
          ) : (
            /* Resultados de búsqueda */
            <div className={styles.resultsContainer}>
              {loading ? (
                <div className={styles.loading}>
                  <div className={styles.loadingSpinner}></div>
                  <p>Buscando...</p>
                </div>
              ) : totalResults === 0 ? (
                <div className={styles.noResults}>
                  <Search size={48} strokeWidth={1.5} />
                  <h2>No encontramos resultados</h2>
                  <p>No hay resultados para "{query}". Intenta con otros términos.</p>
                  <div className={styles.suggestions}>
                    <p>Sugerencias:</p>
                    <ul>
                      <li>Revisa la ortografía</li>
                      <li>Usa palabras más generales</li>
                      <li>Prueba con sinónimos</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <>
                  {/* Resumen de resultados */}
                  <div className={styles.resultsSummary}>
                    <p>
                      Se encontraron <strong>{totalResults}</strong> resultados para 
                      "<strong>{query}</strong>"
                    </p>
                  </div>

                  {/* Tabs */}
                  <div className={styles.tabs}>
                    <button
                      className={`${styles.tab} ${activeTab === 'articles' ? styles.active : ''}`}
                      onClick={() => setActiveTab('articles')}
                    >
                      Artículos ({results.articles.length})
                    </button>
                    {results.categories.length > 0 && (
                      <button
                        className={`${styles.tab} ${activeTab === 'categories' ? styles.active : ''}`}
                        onClick={() => setActiveTab('categories')}
                      >
                        Secciones ({results.categories.length})
                      </button>
                    )}
                    {results.authors.length > 0 && (
                      <button
                        className={`${styles.tab} ${activeTab === 'authors' ? styles.active : ''}`}
                        onClick={() => setActiveTab('authors')}
                      >
                        Autores ({results.authors.length})
                      </button>
                    )}
                  </div>

                  {/* Contenido del tab */}
                  <div className={styles.tabContent}>
                    {activeTab === 'articles' && (
                      <>
                        {/* Filtros */}
                        <div className={styles.filters}>
                          <div className={styles.filterGroup}>
                            <label>Sección:</label>
                            <select 
                              value={selectedCategory}
                              onChange={(e) => setSelectedCategory(e.target.value)}
                            >
                              <option value="all">Todas</option>
                              {categories.map(cat => (
                                <option key={cat.id} value={cat.slug}>
                                  {cat.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className={styles.filterGroup}>
                            <label>Ordenar:</label>
                            <select 
                              value={sortBy}
                              onChange={(e) => setSortBy(e.target.value)}
                            >
                              <option value="relevance">Relevancia</option>
                              <option value="newest">Más recientes</option>
                              <option value="oldest">Más antiguos</option>
                              <option value="popular">Más leídos</option>
                            </select>
                          </div>
                        </div>

                        {/* Lista de artículos */}
                        <div className={styles.articlesList}>
                          {sortedArticles.map(article => (
                            <ArticleCard
                              key={article.id}
                              article={article}
                              variant="horizontal"
                              showExcerpt={true}
                            />
                          ))}
                        </div>
                      </>
                    )}

                    {activeTab === 'categories' && (
                      <div className={styles.categoriesList}>
                        {results.categories.map(category => (
                          <Link
                            key={category.id}
                            to={`/seccion/${category.slug}`}
                            className={styles.categoryResult}
                            style={{ '--cat-color': category.color }}
                          >
                            <span className={styles.categoryResultIcon}>
                              {category.icon}
                            </span>
                            <div>
                              <h3>{category.name}</h3>
                              {category.description && (
                                <p>{category.description}</p>
                              )}
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}

                    {activeTab === 'authors' && (
                      <div className={styles.authorsList}>
                        {results.authors.map(author => (
                          <Link
                            key={author.id}
                            to={`/autor/${author.id}`}
                            className={styles.authorResult}
                          >
                            {author.avatar ? (
                              <img 
                                src={author.avatar} 
                                alt={author.name}
                                className={styles.authorAvatar}
                              />
                            ) : (
                              <div className={styles.authorAvatarPlaceholder}>
                                {author.name.charAt(0)}
                              </div>
                            )}
                            <div>
                              <h3>{author.name}</h3>
                              {author.role && <p>{author.role}</p>}
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
